import { useCallback, useMemo } from "react";

import { IOptionItem } from "../../../../../formik/formik-autocomplete/FormikAutocomplete.types";
import { useSystemDropdownOptions } from "../../../../../hooks/useSystemDropdownOptions";
import { SystemDropdownCategory, SystemDropdownParameter } from "../../../../api/ClientFileApi";
import { IncomeTreatment, IncomeTypeKey, IncomeTypeTraits } from "../IncomeView.types";

const TRAILING_KEYS: string[] = [IncomeTypeKey.Other, IncomeTypeKey.OtherNonSeizable];

const MAIN_GROUP = "main";

export function useIncomeTypes() {
  const { optionItems, labelOf, parameterOf, isLoading } = useSystemDropdownOptions(SystemDropdownCategory.IncomeType);

  const orderedOptions: IOptionItem[] = useMemo(() => {
    const byLabel = [...optionItems].sort((a, b) => a.label.localeCompare(b.label, "de"));
    const main = byLabel.filter((option) => !TRAILING_KEYS.includes(option.id));
    const trailing = TRAILING_KEYS.map((key) => byLabel.find((option) => option.id === key)).filter(
      (option): option is IOptionItem => option !== undefined
    );
    return [...main, ...trailing];
  }, [optionItems]);

  const groupOf = useCallback(
    (option: IOptionItem) => (TRAILING_KEYS.includes(option.id) ? option.id : MAIN_GROUP),
    []
  );

  const traitsOf = useCallback(
    (incomeType: string | null): IncomeTypeTraits => {
      const treatment = parameterOf(incomeType, SystemDropdownParameter.IncomeTreatment);
      return {
        isNonSeizable: treatment === IncomeTreatment.NonSeizable,
        hasDailyRate: treatment === IncomeTreatment.DailyRate,
        expectsSpecialPayments: treatment === IncomeTreatment.SpecialPaymentsExpected,
      };
    },
    [parameterOf]
  );

  return {
    options: orderedOptions,
    groupOf,
    isMainGroup: (group: string) => group === MAIN_GROUP,
    labelOf,
    traitsOf,
    isLoading,
  };
}
